import React from 'react';
import { css } from '@emotion/core';
import { useSelector } from 'react-redux';

import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { mergeRefs } from 'shared/react-utils';
import { getJourney } from 'redux/selectors/learning-journeys';
import ClickableContainer from 'components/clickable-container';
import { doubleSpacing } from 'styles/global_defaults/scaffolding';
import { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import NvSwitch, { NvSwitchRef } from 'shared/components/inputs/nv-switch';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import WelcomeEmailModalWrapper from 'learning_journeys/components/welcome-email-modal-wrapper';
import {
  enableWelcomeEmail,
  disableWelcomeEmail,
  getCourseWelcomeEmail,
} from 'redux/actions/course-communications';
import useDropdownItemInnerFocusables, {
  isVerticalArrowKey,
} from 'shared/components/inputs/nv-dropdown/hooks/use-dropdown-item-inner-focusables';

type Props = {
  catalogId: string,
  onClick?: React.ComponentProps<'div'>['onClick'],
};

const WelcomeEmailDropdownItem = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    onClick,
    catalogId,
  } = props;

  const dispatch = useAppDispatch();
  const openModalRef = React.useRef<Function>();
  const switchRef = React.useRef<NvSwitchRef>();
  const containerRef = React.useRef<HTMLDivElement>();
  const accessibilityDropdownItemProps = useDropdownItemInnerFocusables();
  const {
    ref: accessibilityDropdownItemRef,
    ...restAccessibilityDropdownItemProps
  } = accessibilityDropdownItemProps;

  const {
    id: courseId,
    welcomeEmailEnabled,
  } = useSelector((state) => getJourney(state, catalogId));

  const [checked, setChecked] = React.useState(welcomeEmailEnabled);

  const styles = css`
    .welcome-email-switch {
      margin-left: ${doubleSpacing * 2}px;
    }
  `;

  const handleSwitchClick = (e, newChecked) => {
    e.stopPropagation();

    if (!newChecked) {
      e.preventDefault();

      const handleDialogConfirm = () => switchRef.current.toggle();

      dispatch(openConfirmationDialog({
        cancelText: t.FORM.CANCEL(),
        confirmText: t.FORM.YES_SURE(),
        onConfirm: handleDialogConfirm,
        title: t.COURSE_COMMUNICATIONS.WELCOME_EMAIL.DISABLE.WARNING_TITLE(),
        bodyText: t.COURSE_COMMUNICATIONS.WELCOME_EMAIL.DISABLE.WARNING_TEXT(),
      }));
    }
  };

  const handleSwitchChange = (newChecked) => {
    const oldValue = checked;
    setChecked(newChecked);

    const args = {
      courseId,
      catalogId,
    };

    let actionToDispatch;

    if (newChecked) {
      actionToDispatch = enableWelcomeEmail;
    } else {
      actionToDispatch = disableWelcomeEmail;
    }

    wrapThunkAction(dispatch(actionToDispatch(args)))
      .then(() => {
        if (actionToDispatch === enableWelcomeEmail) {
          dispatch(getCourseWelcomeEmail({ catalogId }))
            .then(({ payload: { result } }) => {
              if (result?.id) {
                openModalRef.current?.(true);
              }
            });
        }
      })
      .catch(() => {
        setChecked(oldValue);
      });
  };

  const handleSwitchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isVerticalArrowKey(e.key)) {
      e.stopPropagation();
    }
  };

  return (
    <WelcomeEmailModalWrapper catalogId={catalogId}>
      {(openModal) => {
        openModalRef.current = openModal;

        const handleItemClick = (e) => {
          onClick?.(e);

          if (welcomeEmailEnabled) {
            openModal(false);
          } else {
            switchRef.current.toggle();
          }
        };

        return (
          <ClickableContainer
            css={styles}
            onClick={handleItemClick}
            ref={mergeRefs(ref, containerRef, accessibilityDropdownItemRef)}
            {...restAccessibilityDropdownItemProps}
          >
            <div className='text-medium'>
              {t.LEARNING_JOURNEYS.DASHBOARD.JOURNEY_OPTIONS.WELCOME_EMAIL()}
            </div>
            <NvSwitch
              ref={switchRef}
              checked={checked}
              onClick={handleSwitchClick}
              onChange={handleSwitchChange}
              onKeyDown={handleSwitchKeyDown}
              className='welcome-email-switch'
            />
          </ClickableContainer>
        );
      }}
    </WelcomeEmailModalWrapper>
  );
});

export const createWelcomeEmailDropdownItem = (props: Props): NvDropdownOption => ({
  type: 'custom',
  preventClosing: true,
  customItem: <WelcomeEmailDropdownItem {...props} />,
});

export default WelcomeEmailDropdownItem;
